import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import axios from "axios";
import { BASE_URL } from "../../../config/config";
// import { ValidateUser } from "../../../redux/reducer/ValidateUser";
import { deleteNotificationThunk, getOneNotification, markAllNotificationsRead, markNotificationRead } from "../../../redux/reducer/GetNotifications";

export function useNotifications() {
  const dispatch = useDispatch();
  const { validateUser } = useSelector((state) => state);
  const { notificationSlice } = useSelector((state) => state);
  const [notifications, setNotifications] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [markingAll, setMarkingAll] = React.useState(false);
  const [markingId, setMarkingId] = React.useState(null);
  const [deletingId, setDeletingId] = React.useState(null);
  const [filter, setFilter] = React.useState("all"); // all | unread | read
  const [deleteConfirm, setDeleteConfirm] = React.useState(null); // id to confirm delete
  const userId = validateUser?.UserValidate?.data?.user?._id;
  React.useEffect(() => {
    if (userId) {
      dispatch(getOneNotification({ notificationId: userId }));
    }
  }, [userId, dispatch]);
  // Load from Redux
  React.useEffect(() => {
    const data = notificationSlice?.notificationDetail?.data?.notifications || [];
    const formatted = data.map((item) => ({
      // id: item?._id || String(Math.random()),
      id: item?._id,
      // The server writes a proper title for every notification; the type-based
      // labels are only a fallback for old records without one.
      title:
        item?.title ||
        (item?.type === "registration"
          ? "New Tenant Registration"
          : item?.type === "payment"
            ? "Payment Received"
            : item?.type === "alert"
              ? "System Alert"
              : "Notification"),
      message: item?.message || "No message",
      date: item?.date || item?.createdAt,
      read: item?.read ?? false,
      type: item?.type || "general",
    }));
    setNotifications(formatted);
    setLoading(false);
  }, [notificationSlice]);

  // Filtered list
  const filtered = React.useMemo(() => {
    if (filter === "unread") return notifications.filter((n) => !n.read);
    if (filter === "read") return notifications.filter((n) => n.read);
    return notifications;
  }, [notifications, filter]);

  const unreadCount = notifications.filter((n) => !n.read).length;
  const allRead = notifications.length > 0 && unreadCount === 0;

  const getToken = () => sessionStorage.getItem("ots_token");

  // Mark single as read
  const markOneAsRead = async (id) => {
    try {
      setMarkingId(id);

      await dispatch(markNotificationRead({ notificationId: id })).unwrap();

      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n))
      );

      toast.success("Marked as read");

    } catch (error) {
      toast.error("Failed to mark as read");
    } finally {
      setMarkingId(null);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      setMarkingAll(true);

      await dispatch(markAllNotificationsRead({ userId })).unwrap();

      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));

      toast.success("All marked as read");

      if (userId) {
        dispatch(getOneNotification({ notificationId: userId }));
      }

    } catch (error) {
      toast.error("Failed to mark all as read");
    } finally {
      setMarkingAll(false);
    }
  };

  // Delete single
  const deleteNotification = async (id) => {
    try {
      setDeletingId(id);

      // ✅ Correct URL, called through dispatch
      const result = await dispatch(
        deleteNotificationThunk({ notificationId: id })
      ).unwrap();

      // Local state update
      setNotifications((prev) => prev.filter((n) => n._id !== id)); // MongoDB uses _id, not id
      toast.success("Notification deleted");

      // Refresh if userId is available
      if (userId) {
        dispatch(getOneNotification({ notificationId: userId }));
      }
    } catch {
      toast.error("Failed to delete notification");
    } finally {
      setDeletingId(null);
      setDeleteConfirm(null);
    }
  };

  // Delete all
  const deleteAll = async () => {
    try {
      setDeletingId("all");
      await axios.delete(`${BASE_URL}/deleteAllNotifications`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setNotifications([]);
      toast.success("All notifications deleted");
      if (userId) {
        dispatch(getOneNotification({ notificationId: userId }));
      }
    } catch {
      toast.error("Failed to delete all");
    } finally {
      setDeletingId(null);
      setDeleteConfirm(null);
    }
  };

  return {
    notifications: filtered,
    allNotifications: notifications,
    loading,
    markingAll,
    markingId,
    deletingId,
    filter,
    setFilter,
    unreadCount,
    allRead,
    deleteConfirm,
    setDeleteConfirm,
    markOneAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAll,
  };
}